import React, { useCallback, useEffect, useState } from 'react';

import clsx from 'clsx';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

import ErrorMessage from 'apps/web_forms/components/commons/ErrorMessage';
import TimeSelect from 'apps/web_forms/components/commons/TimeSelects';
import useChangeValue from 'apps/web_forms/hooks/useChangeValue';
import useResourceUpdateHighlight from 'apps/web_forms/hooks/useResourceUpdateHighlight';
import { selectFieldErrorById } from 'apps/web_forms/hooks/useGlobalSelector';
import useRules from 'apps/web_forms/hooks/useRules';
import { DEVICES_SIZES, EMPTY_DATETIME_VALUE } from 'apps/web_forms/utils/constants';
import customPropTypes from 'apps/web_forms/utils/customPropTypes';
import { getFormattedDate } from 'apps/web_forms/utils/dateTimeFormatter';

const DateTimeContainerElement = styled.div`
  display: flex;
  flex-direction: column;
`;

const DateTimePickerContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  .input-mask {
    border-radius: 0;
    margin-right: 0.9em;
    margin-bottom: 0.9em;
  }
  @media (min-width: ${DEVICES_SIZES.tablet}) {
    .input-mask {
      margin-bottom: 0;
    }
  }
  .react-datepicker__year-select {
    height: 1.5em !important;
    margin-top: 0.5em;
  }
  .react-datepicker__month-select {
    height: 1.5em !important;
    margin-top: 0.5em;
  }
  .react-datepicker-popper {
    z-index: 100;
  }
`;

const DateTimeInput = ({ element, formContext, onChangeValue, toggleActiveValue, showContent }) => {
  const { title, identifier, hint, hidden, validate_msg: validateMsg } = element;
  const { register, getValues } = formContext;

  const { changeValue, currentValue } = useChangeValue(
    element,
    onChangeValue,
    formContext
  );

  const errorMessage = useSelector((state) => selectFieldErrorById(state, identifier));

  const { hasResourceUpdateHighlight, removeResourceUpdateHighlight } = useResourceUpdateHighlight({ fieldIdentifier: identifier });
  
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [date, setDate] = useState('');
  const [hour, setHour] = useState('');
  const [minutes, setMinutes] = useState('');
  const [{ isActive, isRequired, isElementValid, isReadOnly, initialAnswer }] = useRules(element, formContext);

  useEffect(() => {
    register(identifier, {
      validate: () => isElementValid || validateMsg || false,
      required: isRequired ? 'This field is required' : false,
    })
  }, [identifier, isElementValid, validateMsg, isRequired]);

  useEffect(() => {
    if (initialAnswer && isFirstRender){
      const [initialDate, initialTime] = initialAnswer.includes("T") ? initialAnswer.split('T') : initialAnswer.split(" ");
      const [initialHours, initialMinutes] = initialTime.split(':');
      setDate(DateTime.fromISO(initialDate).toJSDate())
      setHour(initialHours);
      setMinutes(initialMinutes);
      changeValue(initialAnswer);
    }else {
      /**
       * This else is needed to trigger the Completion Circle calculations for Group/Subform
       * Ideally we don't want many of these triggers, but keep it in TODO list
      */
      changeValue(null, false);
    }
    
    setTimeout(() => {
      setIsFirstRender(false);
    }, 300);
  }, [initialAnswer]);

  const onDateChange = (newDate) => {
    setDate(newDate);
    if ((!minutes || minutes === EMPTY_DATETIME_VALUE) && (!hour || hour === EMPTY_DATETIME_VALUE)) {
      setHour('00');
      setMinutes('00');
      newDate.setHours('00', '00');
    }else {
      newDate.setHours(hour, minutes);
    }
    changeValue(getFormattedDate(newDate, 'datetime'), true);
  };

  const onCustomHourChange = (newHour) => {
    setHour(newHour);
    const newDate = date ? new Date(date) : new Date();
    
    if (newHour === EMPTY_DATETIME_VALUE) {
      setDate(null);
      setMinutes(null);
      changeValue('', true);
      return;
    } 

    newDate.setHours(newHour);
    
    if ( !minutes || minutes === EMPTY_DATETIME_VALUE) {
      setMinutes('00');
      newDate.setMinutes('00');
    }else{
      newDate.setMinutes(minutes);
    }

    if(!date){
      setDate(newDate);
    }
    

    changeValue(getFormattedDate(newDate, 'datetime'), true);
  };

  const onCustomMinutesChange =(newMinutes) => {
    setMinutes(newMinutes);
    const newDate = date ? new Date(date) : new Date();

    if (newMinutes === EMPTY_DATETIME_VALUE) {
      setDate(null);
      setHour(null);
      changeValue('', true);
      return;
    }
    
    if (!hour || hour === EMPTY_DATETIME_VALUE) {
      setHour('00');
      newDate.setHours('00');
    }else{
      newDate.setHours(hour);
    }

    if(!date){
      setDate(newDate);
    }
   
    newDate.setMinutes(newMinutes);

    changeValue(getFormattedDate(newDate, 'datetime'), true);
  };

  useEffect(() => {
    toggleActiveValue(identifier, isActive);
  }, [isActive]);

  if (!isActive || hidden || !showContent) return null;

  return (
    <div className='react-form-control'>
      <DateTimeContainerElement>
        <label htmlFor={identifier}>{title}</label>
        <DateTimePickerContainer readOnly={isReadOnly}>
          <input
            type='text'
            hidden
            name={identifier}
            id={identifier}
          />
          <DatePicker
            selected={date}
            popperClassName='react-datepicker-popper'
            onChange={onDateChange}
            onFocus={removeResourceUpdateHighlight}
            customInput={
              // eslint-disable-next-line react/jsx-wrap-multilines
              <input
                type='text'
                className={clsx(
                  'input-mask',
                  errorMessage !== undefined && 'invalid',
                  hasResourceUpdateHighlight && 'updateHighlight'
                )}
              />
            }
            placeholderText='Pick a Date'
            readOnly={isReadOnly}
            id={`${identifier}_date`}
            className={`${isReadOnly ? 'read-only' : ''}`}
            autoComplete='off'
            showMonthDropdown
            showYearDropdown
            dropdownMode='select'
          />
          <TimeSelect
            identifier={identifier}
            customHour={hour}
            onCustomHourChange={onCustomHourChange}
            customMinutes={minutes}
            onCustomMinutesChange={onCustomMinutesChange}
            isValid={errorMessage === undefined}
            hasResourceUpdateHighlight={hasResourceUpdateHighlight}
            removeResourceUpdateHighlight={removeResourceUpdateHighlight}
            readOnly={isReadOnly}
            onChange={onChangeValue}
          />
        </DateTimePickerContainer>
        {hint && <p className='element-hint'>{hint}</p>}
        {errorMessage !== undefined && (
          <ErrorMessage id={`${identifier}_errmsg`}>
            {errorMessage}
          </ErrorMessage>
        )}
      </DateTimeContainerElement>
    </div>
  );
};

DateTimeInput.propTypes = {
  element: PropTypes.shape({
    title: PropTypes.string,
    identifier: PropTypes.string,
    type: PropTypes.string,
    hint: PropTypes.string,
    hidden: PropTypes.bool,
    validate_msg: PropTypes.string,
    initialAnswer: PropTypes.string,
  }).isRequired,
  formContext: customPropTypes.formContext.isRequired,
  onChangeValue: PropTypes.func.isRequired,
};

export default React.memo(DateTimeInput);
